import { useMemo, useState } from "react"

import { Text } from "@minuthq/meatball-ui-react"

import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { useFetchHomeGuests } from "src/data/guests/queries/guestQueries"
import { TGuest } from "src/data/guests/types/guestTypes"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { LocalizedDate } from "src/ui/LocalizedDate/LocalizedDate"

const LIMIT = 15

export function ParadiseHomeGuests({
  homeId,
  home,
}: {
  homeId: string
  home: { timezone: string }
}) {
  const [offset, setOffset] = useState(0)

  const { data, isLoading, isError, error } = useFetchHomeGuests({
    homeId,
    filters: {
      limit: LIMIT,
      offset,
    },
  })

  // Table columns
  const columns: TableColumn<TGuest>[] = useMemo(
    () => [
      {
        value: "name",
        label: "Name",
        renderCell: (guest: TGuest) => <span>{guest.name}</span>,
        columnWidth: "auto",
      },
      {
        value: "email",
        label: "Email",
        renderCell: (guest: TGuest) => <span>{guest.email ?? "-"}</span>,
        columnWidth: "auto",
      },
      {
        value: "phone_number",
        label: "Phone",
        renderCell: (guest: TGuest) => <span>{guest.phone_number ?? "-"}</span>,
        columnWidth: "auto",
      },
      {
        value: "from",
        label: "From",
        renderCell: (guest: TGuest) => {
          if (!guest.checkin_time) {
            return "-"
          }
          return (
            <div>
              <Text variant="bodyXS">
                <LocalizedDate
                  date={guest.checkin_time}
                  timezone={home.timezone}
                />
              </Text>
              <Text variant="bodyXS" color="secondary">
                <LocalizedDate date={guest.checkin_time} timezone="UTC" /> UTC
              </Text>
            </div>
          )
        },
        columnWidth: "auto",
      },
      {
        value: "to",
        label: "To",
        renderCell: (guest: TGuest) => {
          if (!guest.checkout_time) {
            return "-"
          }
          return (
            <div>
              <Text variant="bodyXS">
                <LocalizedDate
                  date={guest.checkout_time}
                  timezone={home.timezone}
                />
              </Text>
              <Text variant="bodyXS" color="secondary">
                <LocalizedDate date={guest.checkout_time} timezone="UTC" /> UTC
              </Text>
            </div>
          )
        },
        columnWidth: "auto",
      },
      {
        value: "source",
        label: "Booking Source",
        renderCell: (guest: TGuest) => <span>{guest.source ?? "-"}</span>,
        columnWidth: "auto",
      },
    ],
    [home.timezone]
  )

  const { headerElements, templateColumns, rows } = useTableColumns<TGuest>({
    columns,
    data: data?.guests ?? [],
  })

  return (
    <div>
      <ParadiseTable
        header={headerElements}
        rows={rows}
        templateColumns={templateColumns}
        isLoading={isLoading}
        error={
          isError
            ? {
                hasError: true,
                title: error.message || "Failed to load guests",
              }
            : undefined
        }
        removeMargin
      />
      <ParadisePager
        limit={LIMIT}
        offset={offset}
        setOffset={setOffset}
        totalCount={data?.paging.total_count}
      />
    </div>
  )
}
